import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Button, Space, Upload, MessagePlugin, Loading, Link, Tag } from 'tdesign-react';
import { CloudUploadIcon } from 'tdesign-icons-react';
import dayjs from 'dayjs';
import ListPanel from '@/components/ListPanel';
import Descriptions from '@/components/Descriptions';
import { getLicenseExpiredSoonPromptAsync } from '@/slices/authSlice';
import { getLicenseCache, licenseUpload } from '@/services/licenseService';
import PermWrapper from '@/components/PermWrapper';
import StatsModal from './StatsModal';
import NoData from './NoData';

function License({ getLicenseExpiredSoonPromptAsync }) {
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState();

  const loadData = useMemoizedFn(async () => {
    setLoading(true);
    try {
      const { data } = await getLicenseCache();
      setData(data);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setLoading(false);
    }
  });

  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('licenseFile', file[0]);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await licenseUpload(formData);
        MessagePlugin.success('上传成功！');
        // 简单刷新
        loadData();
        // 过期消息提示刷新
        getLicenseExpiredSoonPromptAsync();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <ListPanel
      header='License管理'
      operators={
        <Space>
          {data === null ? (
            ''
          ) : (
            <PermWrapper resource='pulsar:sys:license:update'>
              <Upload theme='file' onSelectChange={doUpload}>
                <BigButton
                  theme='default'
                  variant='outline'
                  icon={<CloudUploadIcon />}
                  size='large'
                >
                  更新License
                </BigButton>
              </Upload>
            </PermWrapper>
          )}
        </Space>
      }
    >
      <Root>
        {data === null ? (
          <NoData />
        ) : (
          <LicenseInfo>
            <Loading loading={loading}>
              <Descriptions column={3} emptySuffix='-'>
                <Descriptions.Item
                  span={3}
                  label='功能列表'
                  value={
                    <Space size='small'>
                      {data?.functionList.split(',').map((item, i) => {
                        return (
                          <Tag key={i} theme='default'>
                            {item}
                          </Tag>
                        );
                      })}
                    </Space>
                  }
                />
                <Descriptions.Item label='license模型' value={data?.licenseType} />
                <Descriptions.Item
                  label='集群数量限制'
                  value={data?.clusterNum === -1 ? '无限制' : data?.clusterNum}
                />
                <Descriptions.Item label='功能类型' value={data?.functionType} />

                <Descriptions.Item label='生效时间' type='datetime' value={data?.issuedTime} />
                <Descriptions.Item
                  label='过期时间'
                  value={
                    dayjs(data?.expiryTime).format('YYYY-MM-DD') === '1970-01-01'
                      ? '永不过期'
                      : dayjs(data?.expiryTime).format('YYYY-MM-DD HH:mm:ss')
                  }
                />
                <Descriptions.Item
                  label='机器信息'
                  value={
                    <Link theme='primary' onClick={() => setVisible(true)}>
                      查看信息
                    </Link>
                  }
                />
                <Descriptions.Item span={3} label='备注' value={data?.description} />
              </Descriptions>
            </Loading>
          </LicenseInfo>
        )}
      </Root>
      <StatsModal
        json={data?.licenseCheckModel}
        visible={visible}
        handleClose={() => setVisible(false)}
      />
    </ListPanel>
  );
}

export default connect(() => ({}), {
  getLicenseExpiredSoonPromptAsync,
})(License);

const Root = styled.div`
  display: flex;
`;
const LicenseInfo = styled.div`
  width: 100%;
`;

const BigButton = styled(Button)`
  border: none;
  &&& {
    font: var(--td-font-body-medium);
  }
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
`;
